<template>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    label-width="100px"
    class="position-form"
  >
    <el-form-item label="职位名称" prop="title">
      <el-input v-model="formData.title" placeholder="请输入职位名称" clearable />
    </el-form-item>

    <el-form-item label="职位链接" prop="jobUrl">
      <el-input 
        v-model="formData.jobUrl" 
        placeholder="请输入职位招聘链接"
        clearable
      >
        <template #append>
          <el-button v-if="formData.jobUrl" @click="openUrl(formData.jobUrl)">
            <el-icon><Link /></el-icon>
          </el-button>
        </template>
      </el-input>
    </el-form-item>

    <el-form-item label="工作类型" prop="workType">
      <el-select v-model="formData.workType" placeholder="请选择工作类型" class="full-width" clearable>
        <el-option 
          v-for="option in WORK_TYPE_OPTIONS" 
          :key="option.value" 
          :label="option.label" 
          :value="option.value" 
        />
      </el-select>
    </el-form-item>

    <el-form-item label="职位描述">
      <el-input
        v-model="formData.description"
        type="textarea"
        :rows="3"
        placeholder="请输入职位描述"
      />
    </el-form-item>

    <el-form-item label="职位要求">
      <el-input
        v-model="formData.requirements"
        type="textarea"
        :rows="3"
        placeholder="请输入职位要求"
      />
    </el-form-item>

    <el-divider>薪资信息</el-divider>

    <div class="form-row">
      <el-form-item label="最低薪资" class="form-col">
        <el-input-number 
          v-model="formData.minSalary" 
          :precision="2" 
          :step="1000" 
          :min="0"
          controls-position="right"
          class="full-width"
        />
      </el-form-item>

      <el-form-item label="最高薪资" class="form-col">
        <el-input-number 
          v-model="formData.maxSalary" 
          :precision="2" 
          :step="1000" 
          :min="formData.minSalary || 0"
          controls-position="right"
          class="full-width"
        />
      </el-form-item>

      <el-form-item label="币种" class="form-col">
        <el-select v-model="formData.salaryCurrency" placeholder="请选择币种" class="full-width">
          <el-option 
            v-for="option in CURRENCY_OPTIONS" 
            :key="option.value" 
            :label="option.label" 
            :value="option.value" 
          />
        </el-select>
      </el-form-item>
    </div>

    <el-divider>投递信息</el-divider>

    <el-form-item label="投递状态" prop="applicationStatus">
      <el-select v-model="formData.applicationStatus" placeholder="请选择投递状态" class="full-width">
        <el-option 
          v-for="(label, value) in APPLICATION_STATUS_MAP" 
          :key="value" 
          :label="label" 
          :value="value" 
        />
      </el-select>
    </el-form-item>

    <el-form-item label="截止日期">
      <el-date-picker
        v-model="formData.deadline"
        type="date"
        placeholder="请选择截止日期"
        format="YYYY-MM-DD"
        value-format="YYYY-MM-DD"
        class="full-width"
      />
    </el-form-item>

    <el-form-item label="优先级">
      <el-select v-model="formData.priority" placeholder="请选择优先级" class="full-width">
        <el-option 
          v-for="option in PRIORITY_OPTIONS" 
          :key="option.value" 
          :label="option.label" 
          :value="option.value" 
        />
      </el-select>
    </el-form-item>

    <el-form-item>
      <div class="form-buttons">
        <el-button @click="cancelForm">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="loading">提交</el-button>
      </div>
    </el-form-item>
  </el-form>
</template>

<script lang="ts" setup>
import { ref, reactive } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import type { Position } from '@/types/position';
import { 
  APPLICATION_STATUS_MAP, 
  WORK_TYPE_OPTIONS, 
  PRIORITY_OPTIONS,
  CURRENCY_OPTIONS
} from '@/types/position';
import { Link } from '@element-plus/icons-vue';

const props = defineProps<{
  position?: Partial<Position>;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', data: Partial<Position>): void;
  (e: 'cancel'): void;
}>();

const formRef = ref<FormInstance>();
const formData = reactive({
  id: props.position?.id,
  companyId: props.position?.companyId,
  title: props.position?.title || '',
  jobUrl: props.position?.jobUrl || '',
  description: props.position?.description || '',
  requirements: props.position?.requirements || '',
  minSalary: props.position?.minSalary || undefined,
  maxSalary: props.position?.maxSalary || undefined,
  salaryCurrency: props.position?.salaryCurrency || 'CNY',
  workType: props.position?.workType || '',
  applicationStatus: props.position?.applicationStatus || 'not_started',
  deadline: props.position?.deadline || '',
  priority: props.position?.priority || 2,
});

const rules: FormRules = {
  title: [
    { required: true, message: '职位名称不能为空', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
  ],
  jobUrl: [
    { required: true, message: '职位招聘链接不能为空', trigger: 'blur' },
    { 
      pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/, 
      message: '请输入有效的URL', 
      trigger: 'blur' 
    }
  ],
  applicationStatus: [
    { required: true, message: '请选择投递状态', trigger: 'change' }
  ]
};

// 打开URL链接
const openUrl = (url: string) => {
  let finalUrl = url;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    finalUrl = 'https://' + url;
  }
  window.open(finalUrl, '_blank');
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate((valid, fields) => {
    if (valid) {
      emit('submit', {
        ...formData,
        jobUrl: formData.jobUrl ? 
          (formData.jobUrl.startsWith('http') ? formData.jobUrl : `https://${formData.jobUrl}`)
          : '',
      });
    }
  });
};

// 取消表单
const cancelForm = () => {
  emit('cancel');
};

// 暴露方法给父组件
defineExpose({
  submitForm
});
</script>

<style scoped>
.position-form {
  padding: 20px 0;
}

.full-width {
  width: 100%;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.form-col {
  width: 100%;
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-form-item__content) {
  flex-wrap: nowrap;
}

:deep(.el-input-group__append) {
  padding: 0;
  overflow: hidden;
}

:deep(.el-input-group__append button) {
  border: none;
  margin: 0;
  height: 100%;
  border-radius: 0;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style> 